import { Injectable, DOCUMENT, inject } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { WINDOW_TOKEN } from '../../services/window.token';
import { ScriptLoaderAttributes, ScriptLoaderService } from '../../services/script-loader.service';
import { RouterBaseService, StateDeclaration } from '../../services/router/router-base.service';
import { ModalService } from '../../components/modal/modal.service';
import { HelpLinkService } from '../../components/help-link/help-link.service';
import { IfFeatureService } from '../../services/if-feature/if-feature.service';
import { SecurityService } from '../../services/security.service';
import { ConfigurationService } from '../../services/configuration/configuration.service';
import { PreferencesService } from '../../services/preferences/preferences.service';
import { ChatConfig, ChatPagesConfig } from './chat.domain';
import { ChatRestService } from './chat-rest.service';

export type ChatButtonStatus = 'OUR_BUTTON' | 'CHAT_SERVICES_BUTTON' | 'NO_BUTTON';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private readonly scriptLoader = inject(ScriptLoaderService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly browserWindow = inject<Window>(WINDOW_TOKEN);
  private readonly chatRestService = inject(ChatRestService);
  private readonly routerService = inject(RouterBaseService);
  private readonly configurationService = inject(ConfigurationService);
  private readonly ifFeatureService = inject(IfFeatureService);
  private readonly securityService = inject(SecurityService);
  private readonly helpLinkService = inject(HelpLinkService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly modalService = inject(ModalService);

  chatConfig: ChatConfig;
  /**
   * This BehaviourSubject is for the Chat component to know when to display our Chat button
   */
  readonly displayChatButton: BehaviorSubject<boolean>;
  /**
   * This BehaviourSubject is for other components to know when there is EITHER our Chat button
   * or the Common Services chat button on screen
   */
  readonly isChatButtonOnPage: BehaviorSubject<ChatButtonStatus>;
  readonly widgetLoadError: BehaviorSubject<boolean>;
  private readonly chatPagesConfig: ChatPagesConfig;
  private readonly allowedPageRoutes: Array<string> = [];
  private readonly allowedPageNames: Array<string> = [];
  private initialized: boolean = false;
  private readonly featureEnabled: boolean = false;
  private readonly isDIUser: boolean = false;
  private showChatPreference: boolean = false;
  private scriptLoaded: boolean = false;
  private routeMatch: boolean = false;
  private helpOverlayOpen: boolean = false;
  private modalOpen: boolean = false;
  private currentUrl: string;
  private currentPageName: string;
  private dockElement: HTMLElement;
  private buttonElement: HTMLElement;
  private wbrChatOnAllPages: boolean;

  constructor() {
    this.featureEnabled = this.ifFeatureService.getFeatureFlag('wbrChatEnabled');
    this.isDIUser = this.securityService.hasPermission('DIRECT_INVESTING');
    this.displayChatButton = new BehaviorSubject<boolean>(false);
    this.isChatButtonOnPage = new BehaviorSubject<ChatButtonStatus>('NO_BUTTON');
    this.widgetLoadError = new BehaviorSubject<boolean>(false);
    this.chatPagesConfig = this.configurationService.getProperty('chatPagesConfig');
    this.allowedPageNames = this.chatPagesConfig?.allowedPageNames;
    this.allowedPageRoutes = this.chatPagesConfig?.allowedPageRoutes;
    const chatPreferenceFeatureFlag = this.ifFeatureService.getFeatureFlag<boolean>('wbrChatPreferences');
    this.wbrChatOnAllPages = this.ifFeatureService.getFeatureFlag<boolean>('wbrChatOnAllPagesEnabled');
    if (chatPreferenceFeatureFlag) {
      this.showChatPreference = this.preferencesService.getPreloadedUserPreference<boolean>('SHOW_CHAT') ?? false;
    } else {
      // We revert to treating it like there is no preference if the feature flag is off
      this.showChatPreference = true;
    }
  }

  init(): void {
    // This if statement effectively turns off the feature if one of the conditions are false
    // since the displayChatButton subject will always remain false if this doesn't run.
    // We don't want to burden the system with a useless onSuccess hook if the feature shouldn't be running.
    if (this.hasAccessToChat() && this.showChatPreference && (this.allowedPageRoutes?.length > 0 || this.wbrChatOnAllPages)) {
      this.routerService.setChatInit((transitionTo: StateDeclaration): void => {
        const toState = transitionTo;
        const currentRoute = toState?.name;
        // They specifically are asking for the full URL instead of the relative url
        this.currentUrl = this.document?.location?.href;
        // Check that parent pages can be set in case of allowed url being set as "page/account" to indicate permission on all child pages
        // Or that something like page/lti/details/{goalId} would get picked up if we set allowed url as "page/lti/details/"
        this.routeMatch = this.allowedPageRoutes.some((route: string) => currentRoute.includes(route));
        this.updateButtonDisplayed();
        this.updatePageName(currentRoute);
      });
      this.helpLinkService.isHelpOverlayOpen.subscribe((isOpen: boolean): void => {
        this.helpOverlayOpen = isOpen;
        this.updateButtonDisplayed();
        this.handleOverlayLayering();
      });
      this.modalService.areModalsOpen.subscribe((isModalOpen: boolean): void => {
        this.modalOpen = isModalOpen;
        this.handleOverlayLayering();
      });
      this.initialized = true;
    }
  }

  loadWidget(): void {
    // In case the config loaded successfully but the script did not load successfully, only attempt to load the script again
    if (this.chatConfig !== undefined && !this.scriptLoaded) {
      this.loadScript();
    } else {
      this.loadConfig();
    }
  }

  closeChat(): void {
    const tdcw = this.browserWindow['tdcw'];
    if (this.scriptLoaded && tdcw?.CLOSECHAT_CONFIRM instanceof Function) {
      tdcw?.CLOSECHAT_CONFIRM();
    }
  }

  updateChatPreference(preference: boolean): void {
    if((preference ?? null) !== null) {
      this.showChatPreference = preference;
      // Needs to be initialized if user is turning on feature, and it hasn't been done yet
      if (this.showChatPreference && !this.initialized) {
        this.init();
      } else {
        this.updateButtonDisplayed();
      }
    }
  }

  hasAccessToChat(): boolean {
    return this.featureEnabled && this.isDIUser;
  }

  openChat(referringPage?: string): void {
    if (this.scriptLoaded) {
      this.browserWindow['tdcw']?.maximize();
    } else {
      if (referringPage !== undefined) {
        // currentPageName is meant to track the "launch point" of the chat widget so only update if loading the widget
        this.currentPageName = referringPage;
      }
      this.loadWidget();
    }
  }

  private updateButtonDisplayed(): void {
    const showOurButton = (this.routeMatch || this.wbrChatOnAllPages) && !this.scriptLoaded && !this.helpOverlayOpen && this.showChatPreference;
    this.displayChatButton.next(showOurButton);
    if (showOurButton) {
      this.isChatButtonOnPage.next('OUR_BUTTON');
    } else if (this.scriptLoaded) {
      this.isChatButtonOnPage.next('CHAT_SERVICES_BUTTON');
    } else {
      this.isChatButtonOnPage.next('NO_BUTTON');
    }
  }

  private updatePageName(currentRoute: string): void {
    if (currentRoute !== undefined && currentRoute !== '') {
      const index = this.allowedPageRoutes.indexOf(currentRoute);
      if (index >= 0) {
        this.currentPageName = this.allowedPageNames[index];
      } else {
        this.currentPageName = '';
      }
    }
    this.updateUrlConfig();
  }

  /**
   * The url for the page the user is currently viewing is meant to be kept up to date in the configuration
   * so that chat representatives can follow along with where the user is going or what they're viewing.
   * @private
   */
  private updateUrlConfig(): void {
    if (this.chatConfig?.userConfig?.userData !== undefined) {
      this.chatConfig.userConfig.userData.TD_ChatReferrer_Url = this.currentUrl;
      this.dockElement?.setAttribute('userconfig', JSON.stringify(this.chatConfig.userConfig));
    }
  }

  private loadConfig(): void {
    this.chatRestService.getChatConfig().subscribe({
      next: (config: ChatConfig): void => {
        if (config?.url !== undefined) {
          this.chatConfig = config;
          this.loadScript();
        }
      },
      error: () => {
        this.widgetLoadError.next(true);
      }
    });
  }

  private loadScript(): void {
    const script: ScriptLoaderAttributes = {
      src: this.chatConfig.url,
      defer: true
    };

    this.scriptLoader.loadScript(script).then((result: unknown): void => {
      if (result !== undefined) {
        this.dockElement = this.document.createElement('chat-dock');
        this.dockElement.setAttribute('debug', 'false');

        if (this.chatConfig?.userConfig?.userData !== undefined) {
          this.chatConfig.userConfig.userData.TD_ChatReferrer_PageName = this.currentPageName;
          this.chatConfig.userConfig.userData.TD_ChatReferrer_Url = this.currentUrl;
        }

        this.dockElement.setAttribute('userconfig', JSON.stringify(this.chatConfig.userConfig));

        this.dockElement.setAttribute('serverconfig', JSON.stringify(this.chatConfig.serverConfig));

        this.dockElement.setAttribute('widgetConfig', JSON.stringify(this.chatConfig.widgetConfig));

        this.dockElement.addEventListener('chatmessage', (event: Event) => {
          this.onChatMessage(event as CustomEvent);
        });

        this.document.body.appendChild(this.dockElement);

        this.buttonElement = this.document.createElement('chat-button');
        this.document.body.appendChild(this.buttonElement);

        this.scriptLoaded = true;

        // Now that the chat widget is loaded after clicking our button, maximize the chat window
        this.browserWindow['tdcw']?.maximize();
        this.updateButtonDisplayed();
      }
    }, () => {
      this.widgetLoadError.next(true);
    });
  }

  private onChatMessage(event: CustomEvent): void {
    // Only trigger keepalive for Customer or Agent messages, not Bot or System messages
    if (event?.detail?.from === 'Customer' || event?.detail?.from === 'Agent') {
      this.chatRestService.triggerSessionAlive().subscribe();
    }
  }

  private handleOverlayLayering(): void {
    if (this.scriptLoaded) {
      // Find the all the popover layers
      const overlayPopoverLayers = this.modalOpen || this.helpOverlayOpen ? this.document.getElementsByClassName('cdk-overlay-popover') : undefined;
      let modalPaneLayer: Element = undefined;
      if (overlayPopoverLayers && overlayPopoverLayers?.length > 0) {
        // Get the "top" popover layer since multiple can be active at a time (e.g. opening helpfile from a modal)
        const overlayPopoverTopLayer = overlayPopoverLayers.item(overlayPopoverLayers.length - 1);
        // Find the pane which houses the interactable content inside of the popover
        modalPaneLayer = overlayPopoverTopLayer?.getElementsByClassName('cdk-overlay-pane')?.item(0);
      }
      if (modalPaneLayer) {
        modalPaneLayer?.appendChild(this.dockElement);
        modalPaneLayer?.appendChild(this.buttonElement);
      } else {
        this.document.body.appendChild(this.dockElement);
        this.document.body.appendChild(this.buttonElement);
      }
    }
  }
}
